/**
 * Mercado de S.A. — sociedades anônimas da Bahia.
 *
 * A tela responde as mesmas três perguntas do mercado de escolas, com uma
 * diferença jurídica que muda o discurso inteiro: desde 2022 a Lei das S.A.
 * (art. 289, redação da Lei 13.818/2019) NÃO exige mais o órgão oficial.
 * O que se vende aqui é oferta — fé pública, comprovante, diagramação
 * oficial —, não obrigação. Todo número diz de onde veio (regra 10).
 *
 * ## Estabelecimento não é companhia
 *
 * A Receita tem uma linha por CNPJ de 14 dígitos. Das 8.209 linhas de S.A.
 * ativas na BA, ~6.800 são filiais (agências de banco, unidades de
 * distribuidora). Publicidade legal se decide na SEDE — o corte principal da
 * tela é "sede na Bahia", e as companhias de fora aparecem como recorte
 * separado, nomeado como tal.
 */

import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api, type Item, type Lista } from '../api/cliente'
import { podeGerirSistema, useAuth } from '../estado/auth'
import { BarrasHorizontais, Distribuicao } from '../componentes/graficos'
import {
  Aviso,
  Cabecalho,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Selecao,
  Tabela,
  Vazio,
} from '../componentes/ui'

const numero = (n: number): string => n.toLocaleString('pt-BR')

function reais(valor: number): string {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  })
}

/** R$ 119.916.974.507 → "R$ 119,9 bi" — capital social não precisa de centavos. */
function reaisCompacto(valor: number): string {
  if (valor >= 1e9) return `R$ ${(valor / 1e9).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} bi`
  if (valor >= 1e6) return `R$ ${(valor / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`
  return reais(valor)
}

export function formatarCnpjSa(v: string): string {
  return v.length === 14 ? `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}` : v
}

interface Estudo {
  totais: {
    sedeNaBa: number
    abertas: number
    fechadas: number
    contactaveis: number
    comEmail: number
    comTelefone: number
    capitalSedeBa: number
    municipiosComSede: number
    companhiasComPresenca: number
    sedeFora: number
    estabelecimentos: number
    filiais: number
  }
  porMunicipio: Array<{ municipio: string; total: number; capital: number }>
  porSetor: Array<{ setor: string; total: number }>
  porFaixaCapital: Array<{ faixa: string; total: number }>
  porDecada: Array<{ decada: string; total: number }>
  maioresPorCapital: Array<{ cnpj: string; razaoSocial: string; municipio: string; capital: number; natureza: string }>
  sedeForaTop: Array<{ razaoSocial: string; uf: string; filiaisNaBa: number }>
  funil: { naBase: number; contatadas: number; clicaram: number; responderam: number; clientes: number }
  tabelaVigente: {
    descricao: string
    regraPreco: { modo?: string; valorUnitarioCentavos?: number } | null
    vigenciaInicio: string
    vigenciaFim: string | null
    atoAprovacao: string | null
  } | null
}

interface LinhaEmpresa {
  cnpj: string
  razaoSocial: string
  nomeFantasia: string | null
  municipio: string
  natureza: 'aberta' | 'fechada'
  capital: number
  cnaePrincipalDesc: string
  dataInicioAtividade: string | null
  contactavel: boolean
  lead: { id: string; estagio: string; organizacaoId: string | null } | null
}

function ComoCalculado({ children }: { children: ReactNode }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className="text-[11px] font-medium text-marca-700 underline decoration-dotted underline-offset-2 transition hover:text-marca-900"
      >
        {aberto ? 'ocultar a conta' : 'como este número é calculado'}
      </button>
      {aberto && (
        <div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
          {children}
        </div>
      )}
    </div>
  )
}

function Indicador({
  rotulo,
  valor,
  detalhe,
  origem,
  children,
}: {
  rotulo: string
  valor: string
  detalhe?: string
  origem: 'receita' | 'estimativa' | 'nosso'
  children?: ReactNode
}) {
  const fonte = {
    receita: { texto: 'Receita Federal', cor: 'text-marca-700 bg-marca-50' },
    estimativa: { texto: 'estimativa', cor: 'text-amber-800 bg-[color:var(--color-atencao-fundo)]' },
    nosso: { texto: 'dado da plataforma', cor: 'text-emerald-800 bg-[color:var(--color-sucesso-fundo)]' },
  }[origem]

  return (
    <div className="rounded-xl border border-[color:var(--color-borda)] bg-white p-4">
      <p className="text-xs font-medium text-slate-500">{rotulo}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{valor}</p>
      {detalhe && <p className="mt-0.5 text-xs text-slate-500">{detalhe}</p>}
      <span className={`mt-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${fonte.cor}`}>
        {fonte.texto}
      </span>
      {children && <ComoCalculado>{children}</ComoCalculado>}
    </div>
  )
}

const ROTULO_ESTAGIO: Record<string, { texto: string; tom: 'neutro' | 'marca' | 'sucesso' | 'atencao' }> = {
  novo: { texto: 'na base', tom: 'neutro' },
  qualificado: { texto: 'qualificada', tom: 'atencao' },
  em_negociacao: { texto: 'em negociação', tom: 'marca' },
  ganho: { texto: 'cliente', tom: 'sucesso' },
  perdido: { texto: 'perdida', tom: 'neutro' },
}

export function MercadoSa() {
  const { usuario } = useAuth()
  // O enquadramento comercial (DOE não é mais obrigatório para S.A.) é leitura
  // interna da estratégia — fica com quem gere o sistema, não com os perfis
  // operacionais da EGBA.
  const veEnquadramento = podeGerirSistema(usuario?.papel)
  const [busca, setBusca] = useState('')
  const [municipio, setMunicipio] = useState('')
  const [natureza, setNatureza] = useState('')
  const [pagina, setPagina] = useState(1)
  /**
   * Premissa da estimativa de potencial, ajustável na tela porque NÃO existe
   * dado público sobre o tamanho médio de uma demonstração publicada. 30 cm é
   * um chute declarado, não um fato — quem mexer no número vê a conta refazer.
   */
  const [cmPorPublicacao, setCmPorPublicacao] = useState(30)
  const [adesao, setAdesao] = useState(10)

  const { data: r, isLoading, isError } = useQuery({
    queryKey: ['mercado-sa-estudo'],
    queryFn: () => api.get<Item<Estudo>>('/api/v1/mercado-sa/estudo'),
    retry: 1,
  })

  const params = new URLSearchParams({ pagina: String(pagina), porPagina: '50' })
  if (busca.trim()) params.set('busca', busca.trim())
  if (municipio.trim()) params.set('municipio', municipio.trim().toUpperCase())
  if (natureza) params.set('natureza', natureza)

  const { data: lista } = useQuery({
    queryKey: ['mercado-sa-empresas', busca, municipio, natureza, pagina],
    queryFn: () =>
      api.get<Lista<LinhaEmpresa> & { total: number }>(`/api/v1/mercado-sa/empresas?${params}`),
    retry: 1,
  })

  if (isLoading) return <Carregando />
  if (isError || !r) {
    return (
      <Vazio
        titulo="A base da Receita está indisponível"
        descricao="O estudo consulta a base pública de CNPJ neste servidor. Tente de novo em instantes."
      />
    )
  }

  const d = r.dados
  const t = d.totais
  const valorCm = d.tabelaVigente?.regraPreco?.valorUnitarioCentavos ?? null
  const companhiasAderindo = Math.round((t.sedeNaBa * adesao) / 100)
  const potencial = valorCm ? companhiasAderindo * cmPorPublicacao * (valorCm / 100) : null

  const totalLista = lista?.total ?? 0
  const totalPaginas = Math.max(1, Math.ceil(totalLista / 50))

  return (
    <div className="space-y-5">
      {/* --------------------------------------------------------- indicadores */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="S.A. com sede na Bahia"
          valor={numero(t.sedeNaBa)}
          detalhe={`${numero(t.abertas)} de capital aberto · ${numero(t.fechadas)} fechadas`}
          origem="receita"
        >
          <p>
            Empresas <strong>ativas</strong> na base pública da Receita Federal com natureza
            jurídica 204-6 (S.A. aberta) ou 205-4 (S.A. fechada), <strong>matriz</strong> em
            município baiano.
          </p>
          <p>
            Filial não conta aqui: publicidade legal se decide na sede. As companhias de fora
            com operação na Bahia estão no quadro próprio, mais abaixo.
          </p>
        </Indicador>
        <Indicador
          rotulo="Capital social somado"
          valor={reaisCompacto(t.capitalSedeBa)}
          detalhe="das sedes baianas"
          origem="receita"
        >
          <p>
            Soma do capital social declarado à Receita pelas {numero(t.sedeNaBa)} matrizes.
            Capital social é o que os sócios subscreveram — serve de régua de porte, e{' '}
            <strong>não</strong> é faturamento (que não existe em dado público).
          </p>
        </Indicador>
        <Indicador
          rotulo="Contactáveis"
          valor={numero(t.contactaveis)}
          detalhe={`${Math.round((t.contactaveis / Math.max(1, t.sedeNaBa)) * 100)}% das sedes`}
          origem="receita"
        >
          <p>
            Sedes com e-mail ({numero(t.comEmail)}) ou telefone ({numero(t.comTelefone)}) no
            cadastro da Receita. É o contato declarado no CNPJ — costuma ser do contador, o que
            para publicidade legal é a porta certa.
          </p>
        </Indicador>
        <Indicador
          rotulo="Companhias com operação na Bahia"
          valor={numero(t.companhiasComPresenca)}
          detalhe={`${numero(t.sedeFora)} têm sede em outro estado`}
          origem="receita"
        >
          <p>
            Raízes de CNPJ distintas com pelo menos um estabelecimento de S.A. ativo na Bahia:
            as {numero(t.sedeNaBa)} sedes baianas mais {numero(t.sedeFora)} companhias de fora
            operando aqui por {numero(t.filiais)} filiais (agências de banco, unidades de
            energia e varejo).
          </p>
        </Indicador>
      </div>

      {/* ------------------------------------------------------ enquadramento */}
      {veEnquadramento && (
        <Aviso tom="atencao">
          <strong>Desde 2022 a publicação no órgão oficial não é mais exigida das S.A. em
          geral</strong> (Lei 6.404/76, art. 289, redação da Lei 13.818/2019). O que a EGBA
          oferece é publicidade legal <em>no veículo oficial do Estado</em> — fé pública,
          comprovante e diagramação oficial. Toda peça desta vertical é classe{' '}
          <strong>promocional</strong>, nunca obrigacional.
        </Aviso>
      )}

      {/* ---------------------------------------------------------- potencial */}
      <Cartao
        titulo="Potencial de receita — estimativa declarada"
        descricao="Não existe dado público sobre o tamanho médio de uma demonstração publicada. As duas premissas abaixo são ajustáveis, e a conta refaz na hora."
      >
        {valorCm ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <Campo
                rotulo="cm-coluna por publicação (premissa)"
                type="number"
                min={1}
                max={500}
                value={cmPorPublicacao}
                onChange={(e) => setCmPorPublicacao(Math.max(1, Number(e.target.value) || 1))}
              />
              <Campo
                rotulo="Adesão sobre as sedes (%)"
                type="number"
                min={1}
                max={100}
                value={adesao}
                onChange={(e) => setAdesao(Math.min(100, Math.max(1, Number(e.target.value) || 1)))}
              />
              <div className="rounded-xl bg-marca-50 p-3">
                <p className="text-xs font-medium text-marca-700">Potencial anual estimado</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-marca-900">
                  {potencial === null ? '—' : reais(potencial)}
                </p>
                <span className="mt-1 inline-block rounded bg-[color:var(--color-atencao-fundo)] px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  estimativa
                </span>
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
              <p>
                <strong>A conta, aberta:</strong> {numero(t.sedeNaBa)} sedes × {adesao}% de adesão ={' '}
                {numero(companhiasAderindo)} companhias publicando 1 demonstração/ano ×{' '}
                {cmPorPublicacao} cm-coluna × {reais(valorCm / 100)}/cm ({d.tabelaVigente?.descricao}
                {d.tabelaVigente?.atoAprovacao ? ` — ${d.tabelaVigente.atoAprovacao}` : ''}).
              </p>
              <p className="mt-1">
                O preço por cm-coluna é dado da tabela vigente; adesão e tamanho da publicação são
                premissas suas. Balanço é sazonal: as demonstrações concentram no 1º quadrimestre
                (AGO até abril, Lei 6.404/76, art. 132).
              </p>
            </div>
          </div>
        ) : (
          <Aviso tom="atencao">
            Não há tabela de preços vigente para a categoria de balanços — cadastre em Tabelas de
            preços para a estimativa aparecer aqui.
          </Aviso>
        )}
      </Cartao>

      {/* -------------------------------------------------------------- funil */}
      <Cartao
        titulo="Funil da captação"
        descricao="Dado da plataforma. Importada não é interessada: contatada exige envio de campanha; engajou exige clique ou formulário (regra 10)."
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            { rotulo: 'Na base de marketing', valor: d.funil.naBase, dica: 'importadas da Receita — universo, não interesse' },
            { rotulo: 'Contatadas', valor: d.funil.contatadas, dica: 'receberam pelo menos um envio de campanha' },
            { rotulo: 'Clicaram', valor: d.funil.clicaram, dica: 'clicaram em link rastreado de um envio' },
            { rotulo: 'Preencheram formulário', valor: d.funil.responderam, dica: 'submissão em landing page' },
            { rotulo: 'Viraram cliente', valor: d.funil.clientes, dica: 'lead convertido em organização' },
          ].map((f) => (
            <div key={f.rotulo} className="rounded-xl border border-[color:var(--color-borda)] p-3" title={f.dica}>
              <p className="text-xs font-medium text-slate-500">{f.rotulo}</p>
              <p className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{numero(f.valor)}</p>
            </div>
          ))}
        </div>
        {d.funil.contatadas === 0 && (
          <p className="mt-3 text-xs text-slate-500">
            Nenhuma campanha para S.A. foi disparada ainda — a campanha modelo está em rascunho em
            Campanhas. Os zeros acima são honestos, não um erro.
          </p>
        )}
      </Cartao>

      {/* ----------------------------------------------------------- gráficos */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao titulo="Onde estão as sedes" descricao="Municípios com mais S.A. sediadas — Receita Federal.">
          <BarrasHorizontais
            itens={d.porMunicipio.map((m) => ({
              rotulo: m.municipio,
              valor: m.total,
              formatado: numero(m.total),
              detalhe: `capital somado ${reaisCompacto(m.capital)}`,
            }))}
          />
          <p className="mt-2 text-[11px] text-slate-500">
            {numero(t.municipiosComSede)} municípios baianos têm ao menos uma S.A. sediada.
          </p>
        </Cartao>
        <Cartao titulo="Setores" descricao="Atividade principal (CNAE) das sedes — Receita Federal.">
          <BarrasHorizontais
            itens={d.porSetor.map((s) => ({
              rotulo: s.setor.length > 42 ? `${s.setor.slice(0, 40)}…` : s.setor,
              valor: s.total,
              formatado: numero(s.total),
              detalhe: s.setor,
            }))}
            larguraRotulo={210}
          />
        </Cartao>
        <Cartao
          titulo="Porte por capital social"
          descricao="O campo “porte” da Receita não separa S.A. (99% viram “Demais”) — capital social separa."
        >
          <Distribuicao
            itens={d.porFaixaCapital.map((f) => ({
              rotulo: f.faixa,
              valor: f.total,
              dica: `${numero(f.total)} companhias`,
            }))}
          />
        </Cartao>
        <Cartao titulo="Quando abriram" descricao="Década de início de atividade das sedes — Receita Federal.">
          <Distribuicao
            itens={d.porDecada.map((dec) => ({
              rotulo: dec.decada,
              valor: dec.total,
              dica: `${numero(dec.total)} companhias`,
            }))}
          />
        </Cartao>
      </div>

      {/* ------------------------------------------------------------ maiores */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao
          titulo="As 10 maiores por capital social"
          descricao="Sedes baianas. Clique para abrir a ficha completa."
        >
          <Tabela>
            <Cabecalho colunas={['Companhia', 'Município', 'Capital social']} />
            <tbody>
              {d.maioresPorCapital.map((m) => (
                <tr key={m.cnpj} className="border-b border-[color:var(--color-borda)] last:border-0">
                  <td className="py-2 pr-3">
                    <Link to={`/mercado-sa/${m.cnpj}`} className="font-medium text-marca-700 hover:underline">
                      {m.razaoSocial}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {formatarCnpjSa(m.cnpj)} · capital {m.natureza === 'aberta' ? 'aberto' : 'fechado'}
                    </p>
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{m.municipio}</td>
                  <td className="py-2 text-right font-medium tabular text-slate-900">
                    {reaisCompacto(m.capital)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Cartao>
        <Cartao
          titulo="De fora, operando aqui"
          descricao="Companhias com sede em outro estado e mais filiais na Bahia. A publicidade legal delas se decide na sede — são relacionamento, não alvo direto."
        >
          <Tabela>
            <Cabecalho colunas={['Companhia', 'Sede', 'Filiais na BA']} />
            <tbody>
              {d.sedeForaTop.map((f) => (
                <tr key={f.razaoSocial} className="border-b border-[color:var(--color-borda)] last:border-0">
                  <td className="py-2 pr-3 font-medium text-slate-800">{f.razaoSocial}</td>
                  <td className="py-2 pr-3 text-slate-600">{f.uf || '—'}</td>
                  <td className="py-2 text-right tabular text-slate-900">{numero(f.filiaisNaBa)}</td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Cartao>
      </div>

      {/* -------------------------------------------------------------- lista */}
      <Cartao
        titulo="Todas as S.A. com sede na Bahia"
        descricao={`${numero(totalLista)} companhias no recorte atual, ordenadas por capital social. Clique para abrir a ficha completa.`}
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Campo
            rotulo="Buscar"
            placeholder="Razão social, fantasia ou CNPJ"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value)
              setPagina(1)
            }}
          />
          <Campo
            rotulo="Município"
            placeholder="ex.: Salvador"
            value={municipio}
            onChange={(e) => {
              setMunicipio(e.target.value)
              setPagina(1)
            }}
          />
          <Selecao
            rotulo="Natureza"
            value={natureza}
            onChange={(e) => {
              setNatureza(e.target.value)
              setPagina(1)
            }}
            opcoes={[
              { valor: '', texto: 'Todas' },
              { valor: 'aberta', texto: 'Capital aberto (204-6)' },
              { valor: 'fechada', texto: 'Capital fechado (205-4)' },
            ]}
          />
        </div>

        {lista && lista.dados.length === 0 ? (
          <Vazio titulo="Nenhuma S.A. neste recorte" descricao="Ajuste a busca ou os filtros." />
        ) : (
          <Tabela>
            <Cabecalho colunas={['Companhia', 'Município', 'Setor', 'Capital social', 'Situação na base']} />
            <tbody>
              {(lista?.dados ?? []).map((e) => {
                const situacao = e.lead
                  ? e.lead.organizacaoId
                    ? { texto: 'cliente', tom: 'sucesso' as const }
                    : (ROTULO_ESTAGIO[e.lead.estagio] ?? { texto: e.lead.estagio, tom: 'neutro' as const })
                  : { texto: 'fora da base', tom: 'neutro' as const }
                return (
                  <tr key={e.cnpj} className="border-b border-[color:var(--color-borda)] last:border-0">
                    <td className="py-2 pr-3">
                      <Link to={`/mercado-sa/${e.cnpj}`} className="font-medium text-marca-700 hover:underline">
                        {e.razaoSocial}
                      </Link>
                      <p className="text-xs text-slate-500 tabular">
                        {formatarCnpjSa(e.cnpj)}
                        {e.nomeFantasia ? ` · ${e.nomeFantasia}` : ''}
                      </p>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{e.municipio}</td>
                    <td className="max-w-52 truncate py-2 pr-3 text-xs text-slate-600" title={e.cnaePrincipalDesc}>
                      {e.cnaePrincipalDesc}
                    </td>
                    <td className="py-2 pr-3 text-right tabular text-slate-900">{reaisCompacto(e.capital)}</td>
                    <td className="py-2">
                      <Etiqueta tom={situacao.tom}>{situacao.texto}</Etiqueta>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Tabela>
        )}

        {totalPaginas > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <button
              type="button"
              disabled={pagina <= 1}
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 font-medium text-slate-700 transition enabled:hover:bg-slate-50 disabled:opacity-40"
            >
              ‹ Anterior
            </button>
            <span className="text-xs text-slate-500">
              página {pagina} de {numero(totalPaginas)}
            </span>
            <button
              type="button"
              disabled={pagina >= totalPaginas}
              onClick={() => setPagina((p) => p + 1)}
              className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 font-medium text-slate-700 transition enabled:hover:bg-slate-50 disabled:opacity-40"
            >
              Próxima ›
            </button>
          </div>
        )}
      </Cartao>
    </div>
  )
}
